import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter, Outlet } from 'react-router-dom';

// Tracking
import * as Sentry from '@sentry/react';
import './index.css';

// Intercom
import Intercom from '@intercom/messenger-js-sdk';

// Contexts
import { AuthProvider } from './contexts/AuthContext.jsx';
import { CustomNotificationProvider } from './contexts/CustomNotificationContext.jsx';

// Components
import Header from './components/Header.jsx';

// Libraries
import ProtectedRoute from './libs/ProtectedRoute.jsx';

// Pages
import * as Pages from './pages';

const INTERCOM_APP_ID = 'sqmzduox';
const DEV_ENV = import.meta.env.DEV;

// Init
if (!DEV_ENV) {
  Sentry.init({
    dsn: 'https://99918e2f2a4128f9cf3e19cae3dbd37b@o4506893748666368.ingest.us.sentry.io/4506893752729600',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [/^https:\/\/muffindata\.com/],
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
  });
}

Intercom({
  app_id: INTERCOM_APP_ID,
  hide_default_launcher: true,
});

const AppLayout = () => (
  <>
    <Header />
    <Outlet />
  </>
);

// Routes
const router = createBrowserRouter([
  // Public
  { path: "/", element: <Pages.Signin /> },
  { path: "/signin", element: <Pages.Signin /> },
  { path: "/forgot-password", element: <Pages.ForgotPassword /> },
  { path: "/request-link", element: <Pages.RequestNewOTP /> },
  { path: "/error", element: <Pages.ErrorPage /> },
  { path: "/confirm-signin", element: <Pages.ConfirmIntent /> },

  // Protected
  {
    path: '/',
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: 'home', element: <Pages.Home /> },
          { path: 'analytics', element: <Pages.Analytics /> },
          { path: 'analytics/*', element: <Pages.DashboardEmbed /> },
          { path: 'integrations', element: <Pages.Integrations /> },
          {
            path: 'integrations/:integrationId',
            element: <Pages.Integration />,
          },
          { path: 'connect-shopify-initialization', element: <Pages.ConnectShopifyInitialization /> },
          { path: 'connect-shopify-success', element: <Pages.ConnectShopifySuccess /> },
          { path: 'products', element: <Pages.Products /> },
          { path: 'set-password', element: <Pages.SetPassword /> },
          { path: 'settings', element: <Pages.Settings /> },
          { path: 'users', element: <Pages.Users /> },
          { path: 'dh-gsc-mapping', element: <Pages.DHGSCMapping /> },
        ],
      },
    ],
  },

  // Fallback
  { path: '*', element: <Pages.NotFoundPage /> },
]);

function Main() {
  return (
    <AuthProvider>
      <CustomNotificationProvider>
        <RouterProvider router={router} />
      </CustomNotificationProvider>
    </AuthProvider>
  );
}

const root = createRoot(document.getElementById('root'));
root.render(<Main />);
